import { CompanyDisplayLabels, PLUGIN_KIND, PluginKind, Team, User } from '@run-diffusion/shared';
import { buildPluginsFeedbackFormUrl } from './pluginsFeedback';

/**
 * One plugin entry, rendered as a <plugin-card> on both plugin surfaces:
 *   - the all-users Plugins page (web/src/views/Plugins/PluginsPage.vue)
 *   - the team "Plugins & API Integrations" page (the enterprise variant)
 * The data lives here as the single source of truth so the two surfaces never
 * drift on copy, marketplace links, or lifecycle status.
 */
export interface PluginCardData {
	name: string;
	icon: string;
	description: string;
	kind: PluginKind;
	status: 'coming-soon' | 'beta' | 'launched';
	feedbackUrl: string;
	marketplaceUrl: string;
	marketplaceCtaText: string;
	tutorialUrl: string;
	// Numbered install steps shown in the beta dialog, for a host whose store has no
	// web page worth linking to. Omit for plugins whose marketplace listing explains itself.
	installSteps?: string[];
	// A public web page for the plugin, shown as a secondary link in the beta dialog.
	// Distinct from marketplaceUrl: that one is the install action (for Rhino, an app
	// protocol handoff that installs in one click), while this is a page to read, link,
	// or fall back to when the handoff does not fire. Omit when the marketplace URL is
	// already the web listing, as it is for Photoshop and Miro.
	listingUrl?: string;
	listingLinkText?: string;
}

/**
 * Builds the shared plugin catalog. Takes the current user + team so each entry's
 * feedback form URL opens prefilled with their context. `team` may be null for a
 * Personal Account — buildPluginsFeedbackFormUrl handles the null case.
 * `displayLabels` supplies the company's name for a team in the card copy.
 */
export const buildPluginsCatalog = (user: User, team: Team | null, displayLabels: CompanyDisplayLabels): PluginCardData[] => {
	return [
		{
			name: 'Adobe Photoshop',
			icon: require('@/assets/plugins/photoshop.svg'),
			description: `
				<p>Edit and generate AI images without leaving Photoshop. Select a region or use your whole document, choose a RunDiffusion tool, and place the result back on your canvas. Start from a text prompt when you need something new, then keep refining it with the same tools.</p>
				<h4>Highlights</h4>
				<ul>
					<li><strong>Edit in place</strong>: Use image-to-image and canvas edits on your active document or a selection, with aspect snapping.</li>
					<li><strong>Generate from text</strong>: Create new images from a prompt and place them on your canvas.</li>
					<li><strong>Access your images</strong>: Browse Library, Uploads, and Recent, then place a result on your canvas or reuse it as input.</li>
					<li><strong>Export your work</strong>: Send your active document to RunDiffusion in one click.</li>
					<li><strong>Preview costs</strong>: See the token cost before you generate.</li>
					<li><strong>Compatibility</strong>: Photoshop 2022 (version 23.3) or later.</li>
				</ul>
			`,
			kind: PLUGIN_KIND.PHOTOSHOP,
			status: 'beta',
			feedbackUrl: buildPluginsFeedbackFormUrl(user, team),
			marketplaceUrl: 'https://exchange.adobe.com/apps/cc/4ce96265/rundiffusion-for-photoshop',
			marketplaceCtaText: 'Get it on Adobe Exchange',
			tutorialUrl: 'https://www.rundiffusion.com/rundiffusion-photoshop-plugin',
		},
		{
			name: 'Miro',
			icon: require('@/assets/plugins/miro.svg'),
			description: `
				<p>Generate AI images directly on your Miro board. Start from a text prompt or from images already on your board, choose a RunDiffusion tool, and place the results next to your source.</p>
				<h4>Highlights</h4>
				<ul>
					<li><strong>Generate from text</strong>: Create images from a prompt without selecting anything on the board.</li>
					<li><strong>Generate from your board</strong>: Use one or more selected board images as input for image-to-image tools.</li>
					<li><strong>Choose your inputs</strong>: Pick images from your board selection, Library, or Uploads.</li>
					<li><strong>Access your images</strong>: Browse past generations and uploads in Library and drag any of them onto the board.</li>
					<li><strong>Place results</strong>: Add each result next to its source, on the side and with the padding you choose.</li>
					<li><strong>Preview costs</strong>: See the token cost before you generate.</li>
				</ul>
			`,
			kind: PLUGIN_KIND.MIRO,
			status: 'beta',
			feedbackUrl: buildPluginsFeedbackFormUrl(user, team),
			marketplaceUrl: 'https://miro.com/app-install/?response_type=code&client_id=3458764674423843806&redirect_uri=%2Fapp-install%2Fconfirm%2F',
			marketplaceCtaText: 'Get it on Miro',
			tutorialUrl: 'https://www.rundiffusion.com/how-to-install-and-use-rundiffusion-miro-plugin',
		},
		{
			name: 'Rhino',
			icon: require('@/assets/plugins/rhino.svg'),
			description: `
				<p>Create AI renders from your viewports without leaving Rhino. Frame a view, choose a RunDiffusion tool, describe what you want, and review the result in a panel beside your viewports. Keep modeling while renders process in the background.</p>
				<h4>Highlights</h4>
				<ul>
					<li><strong>Capture any view</strong>: Use a viewport or named view in your current display mode, or capture only the objects you select.</li>
					<li><strong>Create 3D models</strong>: Generate a 3D model from a view or a prompt, then insert it into your scene at the point and size you choose, on its own layer.</li>
					<li><strong>Choose your tools</strong>: Browse the RunDiffusion tool catalog and adjust each model's settings.</li>
					<li><strong>Preview costs</strong>: See the token cost before you generate.</li>
					<li><strong>Reuse your results</strong>: Use any result as the input for your next generation.</li>
					<li><strong>Script commands</strong>: Add capture and generate to a toolbar button, alias, or macro.</li>
					<li><strong>Compatibility</strong>: Rhino 8 on Windows and Mac.</li>
				</ul>
			`,
			kind: PLUGIN_KIND.RHINO,
			status: 'beta',
			feedbackUrl: buildPluginsFeedbackFormUrl(user, team),
			// Rhino's Package Manager is the only place the package is served, so the CTA hands
			// off to Rhino itself: Rhino 8 registers this protocol and opens Package Manager
			// searching for the package. The install steps below cover a browser that will not
			// open it. The Food4Rhino listing is a web page rather than a second download: it was
			// created from the same Yak package, so its own Install button opens Package Manager
			// too. It stays a secondary link so the one-click handoff keeps the CTA.
			marketplaceUrl: 'rhino8://package/search?name=RunDiffusion',
			marketplaceCtaText: 'Open in Rhino',
			tutorialUrl: '',
			installSteps: [
				'In Rhino 8, run the PackageManager command.',
				'Tick "Include pre-releases", since RunDiffusion for Rhino is in beta.',
				'Search for RunDiffusion, click Install, and restart Rhino.',
				'Run the RunDiffusion command to open the panel and sign in.',
			],
			listingUrl: 'https://www.food4rhino.com/en/app/rundiffusion',
			listingLinkText: 'View the listing on Food4Rhino',
		},
		{
			name: 'Autodesk Revit',
			icon: require('@/assets/plugins/revit.svg'),
			description: `
				<p>Create AI visualizations from your model's views without leaving Revit. Choose a view and a RunDiffusion tool, describe what you want, and compare the result beside your model. Keep modeling while renders process in the background.</p>
				<h4>Highlights</h4>
				<ul>
					<li><strong>Render model views</strong>: Use your active 3D view or choose another view, with framing set in the preview.</li>
					<li><strong>Keep working</strong>: Run multiple renders in the background and track their progress.</li>
					<li><strong>Compare results</strong>: Review the original view and generated image side by side in Studio, then save or send results to RunDiffusion.</li>
					<li><strong>Choose your tools</strong>: Browse the RunDiffusion tool catalog and adjust each model's settings.</li>
					<li><strong>Preview costs</strong>: See the token cost before rendering and bill to your personal or ${displayLabels.team} account.</li>
					<li><strong>Access your images</strong>: Browse renders saved on your computer in Library. Use Uploads to access your account's images or upload images from your computer.</li>
					<li><strong>Compatibility</strong>: Revit 2024–2027 on Windows.</li>
				</ul>
			`,
			kind: PLUGIN_KIND.REVIT,
			status: 'beta',
			feedbackUrl: buildPluginsFeedbackFormUrl(user, team),
			// The Autodesk listing is a web page that explains itself, so it needs no
			// installSteps and no separate listingUrl, same as Photoshop and Miro.
			marketplaceUrl: 'https://marketplace.autodesk.com/apps/0bd81a6e-65f0-4dde-8010-0bb085542174',
			marketplaceCtaText: 'View on Autodesk App Store',
			tutorialUrl: '',
		},
		{
			name: 'NVIDIA Omniverse',
			// Placeholder art, not NVIDIA's mark. See the note inside the SVG.
			icon: require('@/assets/plugins/omniverse.svg'),
			description: `
				<p>Generate AI images from your viewport and add generated 3D assets to your scene from the RunDiffusion panel. Capture a view, choose a tool, and generate while you continue working. Your scene changes only when you choose to make a change.</p>
				<h4>Highlights</h4>
				<ul>
					<li><strong>Capture your view</strong>: Use your viewport or an existing scene camera as the starting image. Your capture stays fixed as you continue navigating.</li>
					<li><strong>Find your tools</strong>: Browse tools by thumbnail, explore kits, or search the catalog.</li>
					<li><strong>Preview costs</strong>: See the cost as you adjust settings and confirm the billing account before generating.</li>
					<li><strong>Keep working</strong>: Generate in the background, track progress, and find completed results in Library.</li>
					<li><strong>Reuse your results</strong>: Compare images with their source captures, use a result as your next input, or add a generated 3D asset to your scene.</li>
					<li><strong>Arrange your workspace</strong>: Dock the RunDiffusion panel or open Library and Uploads in separate windows.</li>
				</ul>
			`,
			kind: PLUGIN_KIND.OMNIVERSE,
			status: 'beta',
			feedbackUrl: buildPluginsFeedbackFormUrl(user, team),
			// The best route is now the Kit Community Registry, which carries this
			// extension: the user installs from inside their own app rather than
			// copying a folder. That is an in-app action and not a URL, so it lives in
			// installSteps and there is no marketplace link to give it. The registry
			// has no per-extension web page to point at either: NVIDIA publishes one
			// directory page for the whole registry, with no anchor per extension.
			//
			// So marketplaceUrl keeps the GitHub release as the SECONDARY route. It is
			// the fallback that always works, including for an app whose Extensions
			// window lists no registry to search, which a Kit app is out of the box.
			marketplaceUrl: 'https://github.com/runnitai/rd-omniverse-ext/releases/latest',
			marketplaceCtaText: 'View Downloads on GitHub',
			tutorialUrl: '',
			installSteps: [
				'Open your Omniverse application and go to Window > Extensions.',
				'Search for RunDiffusion, then select Install.',
				'Turn on Autoload for RunDiffusion so it loads when the application starts. If the RunDiffusion panel does not appear, enable RunDiffusion in the same window.',
				'Select Sign in in the RunDiffusion panel, then follow the instructions in your browser to approve the device.',
				'If RunDiffusion does not appear in your search, check the Extension Registries list in the same window. If no community registry is listed, add one named kit/community with the URL https://dw290v42wisod.cloudfront.net/exts/kit/community',
			],
			listingUrl: 'https://github.com/runnitai/rd-omniverse-ext',
			listingLinkText: 'View Project on GitHub',
		},
	];
};
